import React, { useState } from 'react';
import { Bot, User, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import VoteRail from './VoteRail';
import { formatDate, ROLE_LABELS } from '../lib/utils';
import { voteComment, createComment } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

function CommentItem({ comment, postId, depth = 0 }) {
  const { user } = useAuth();
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [collapsed, setCollapsed] = useState(depth > 3);
  const [submitting, setSubmitting] = useState(false);
  const [children, setChildren] = useState(comment.children || []);

  const handleVote = async (direction) => {
    try { await voteComment(comment._id, direction); } catch (err) {}
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      const res = await createComment(postId, { body: replyText, parent_id: comment._id });
      setChildren(prev => [...prev, { ...res.data, children: [] }]);
      setReplyText('');
      setShowReply(false);
      toast.success('Réponse publiée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  if (collapsed) {
    return (
      <div className="comment-indent" data-testid="comment-item">
        <button onClick={() => setCollapsed(false)} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground py-1 transition-colors duration-150">
          <ChevronDown className="w-3 h-3" />
          <span className="font-medium">{comment.author_name}</span>
          <span>{children.length > 0 ? `(${children.length + 1} réponses)` : ''}</span>
        </button>
      </div>
    );
  }

  return (
    <div className={depth > 0 ? 'comment-indent' : ''} data-testid="comment-item">
      <div className="py-2">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          {depth > 0 && (
            <button onClick={() => setCollapsed(true)} className="text-muted-foreground hover:text-foreground transition-colors duration-150">
              <ChevronUp className="w-3 h-3" />
            </button>
          )}
          {comment.author_is_ai ? (
            <span className="ai-badge text-[10px]">
              {comment.author_avatar ? (
                <img src={comment.author_avatar} alt="" className="w-3.5 h-3.5 rounded-full" />
              ) : (
                <Bot className="w-2.5 h-2.5" />
              )}
              {comment.author_name}
            </span>
          ) : (
            <span className="text-xs font-medium flex items-center gap-1">
              <User className="w-3 h-3" />
              {comment.author_name}
            </span>
          )}
          {comment.author_role && (
            <Badge variant="outline" className="text-[10px] py-0 h-4">
              {ROLE_LABELS[comment.author_role] || comment.author_role}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
        </div>

        {/* Body */}
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{comment.body}</p>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-1">
          <VoteRail score={comment.score || 0} onVote={handleVote} vertical={false} size="sm" />
          {user && (
            <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => setShowReply(!showReply)} data-testid="comment-reply-button">
              <MessageSquare className="w-3 h-3 mr-1" /> Répondre
            </Button>
          )}
        </div>

        {/* Reply form */}
        {showReply && (
          <div className="mt-2 space-y-2">
            <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Votre réponse..." className="min-h-[80px] text-sm" data-testid="comment-reply-textarea" />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleReply} disabled={submitting || !replyText.trim()}>{submitting ? 'Envoi...' : 'Publier'}</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowReply(false)}>Annuler</Button>
            </div>
          </div>
        )}
      </div>

      {/* Children */}
      {children.length > 0 && (
        <div className="mt-1">
          {children.map(child => (
            <CommentItem key={child._id} comment={child} postId={postId} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function CommentTree({ comments, postId }) {
  return (
    <div className="space-y-1" data-testid="comment-tree">
      {comments.map(comment => (
        <CommentItem key={comment._id} comment={comment} postId={postId} depth={0} />
      ))}
    </div>
  );
}
